import React, { useState, useEffect, useCallback } from 'react';
import { Card, Progress, Statistic, Row, Col, Tag, Tooltip, Alert, Space, Button, Typography } from 'antd';
import { ThunderboltOutlined, CheckCircleOutlined, WarningOutlined, CloseCircleOutlined, ReloadOutlined, CrownOutlined } from '@ant-design/icons';
import { API_BASE, authHeaders } from '../config/api';
import { hasFeatureAccess } from './utils/planRestrictions';
import './admin.css';

const { Text } = Typography;

const LiveExperienceScore = ({ currentPlan, compact = false }) => {
  const [score, setScore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const planName = currentPlan?.plan_name || '';
  const normalizedPlan = planName.toLowerCase();
  
  // Direct check for Enterprise/Growth/White Label plans
  const hasAccess = normalizedPlan.includes('enterprise') || 
                    normalizedPlan.includes('growth') || 
                    normalizedPlan.includes('white label') ||
                    hasFeatureAccess(currentPlan, 'live_experience_score');

  const fetchScore = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/analytics/live-experience-score`, {
        headers: { 'Content-Type': 'application/json', ...authHeaders() }
      });
      
      if (res.ok) {
        const data = await res.json();
        setScore(data.score);
        setMetrics(data.metrics);
        setLastUpdated(new Date());
      }
    } catch (err) {
      console.error('[LiveExperienceScore] Fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Always try to fetch - let API decide access
    fetchScore();
    
    // Auto-refresh every 5 seconds
    const interval = setInterval(fetchScore, 5000);
    return () => clearInterval(interval);
  }, [fetchScore]);

  const getScoreColor = (score) => {
    if (score >= 80) return '#52c41a'; // Green
    if (score >= 60) return '#faad14'; // Orange
    return '#ff4d4f'; // Red
  };

  const getScoreStatus = (score) => {
    if (score >= 80) return { text: 'Excellent', icon: <CheckCircleOutlined /> };
    if (score >= 60) return { text: 'Good', icon: <WarningOutlined /> };
    return { text: 'Needs Attention', icon: <CloseCircleOutlined /> };
  };

  // Locked state for users without access — compact card
  if (!hasAccess) {
    return (
      <Card 
        size="small"
        style={{ 
          borderRadius: 12,
          background: 'linear-gradient(135deg, rgba(114, 46, 209, 0.04), rgba(114, 46, 209, 0.01))',
          border: '1px dashed rgba(114, 46, 209, 0.25)'
        }}
        bodyStyle={{ padding: '14px 18px' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <ThunderboltOutlined style={{ fontSize: 18, color: '#722ed1' }} />
            <div>
              <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>
                Live Experience Score <Tag color="purple" style={{ marginLeft: 6, fontSize: 10, lineHeight: '16px', borderRadius: 4 }}>Enterprise</Tag>
              </div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>Real-time UX health monitoring (0-100)</div>
            </div>
          </div>
          <Button size="small" type="primary" icon={<CrownOutlined />} style={{ borderRadius: 6, fontSize: 12 }}>
            Upgrade
          </Button>
        </div>
      </Card>
    );
  }

  // Compact view for dashboard - matches other metric cards
  if (compact) {
    return (
      <Card
        hoverable
        style={{ borderRadius: 14, cursor: 'pointer', height: '100%', border: '1px solid #e0e7ff' }}
        bodyStyle={{ display: 'flex', alignItems: 'center', gap: 12, height: '100%' }}
      >
        <div style={{
          width: 40, 
          height: 40, 
          borderRadius: 10,
          background: score !== null ? (score >= 80 ? '#f0fdf4' : score >= 60 ? '#fffbeb' : '#fef2f2') : '#6366f122', 
          color: score !== null ? getScoreColor(score) : '#6366f1',
          display: 'grid', 
          placeItems: 'center', 
          fontSize: 18
        }}>
          <ThunderboltOutlined />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <Text strong style={{ fontSize: 14, color: '#0f172a' }}>Live Experience Score</Text>
            <Tag color="blue" style={{ fontSize: 10, padding: '0 4px', lineHeight: '16px' }}>LIVE</Tag>
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, color: score !== null ? getScoreColor(score) : '#94a3b8' }}>
            {loading ? 'Loading...' : score !== null ? `${score}/100` : 'N/A'}
          </div>
          {lastUpdated && (
            <Text type="secondary" style={{ fontSize: 11 }}>Updated {lastUpdated.toLocaleTimeString()}</Text>
          )}
        </div>
      </Card>
    );
  }

  // Full view
  return (
    <Card
      title={
        <Space>
          <ThunderboltOutlined style={{ color: '#6366f1' }} />
          <span>Live Experience Score</span>
          <Tag color="blue">LIVE - Updates every 5s</Tag>
          <Tag color="purple">{planName}</Tag>
        </Space>
      }
      extra={
        <Button 
          icon={<ReloadOutlined />} 
          onClick={fetchScore}
          loading={loading}
          size="small"
        >
          Refresh
        </Button>
      }
      style={{ borderRadius: 14 }}
      loading={loading}
    >
      <Alert
        message="Real-Time Monitoring Active"
        description="Your Live Experience Score aggregates page speed, Core Web Vitals, error rates, and user satisfaction. Score updates automatically every 5 seconds."
        type="info"
        showIcon
        closable
        style={{ marginBottom: 24 }}
      />

      <Row gutter={[24, 24]}>
        <Col xs={24} md={12}>
          <div style={{ textAlign: 'center' }}>
            <Progress
              type="dashboard"
              percent={score || 0}
              strokeColor={getScoreColor(score || 0)}
              strokeWidth={8}
              width={200}
              format={(percent) => (
                <div>
                  <div style={{ fontSize: 48, fontWeight: 700, color: getScoreColor(percent) }}>{percent}</div>
                  <div style={{ fontSize: 14, color: '#94a3b8', marginTop: 4 }}>/ 100</div>
                </div>
              )}
            />
            <div style={{ marginTop: 16 }}>
              <Tag 
                color={score >= 80 ? 'success' : score >= 60 ? 'warning' : 'error'} 
                icon={getScoreStatus(score || 0).icon}
                style={{ fontSize: 14, padding: '4px 12px' }}
              >
                {getScoreStatus(score || 0).text}
              </Tag>
            </div>
            {lastUpdated && (
              <div style={{ marginTop: 12, fontSize: 12, color: '#94a3b8' }}>
                Last updated: {lastUpdated.toLocaleTimeString()}
              </div>
            )}
          </div>
        </Col>

        <Col xs={24} md={12}>
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Statistic
              title={
                <Tooltip title="Largest Contentful Paint - measures loading performance">
                  <Space>
                    <span>Page Speed</span>
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  </Space>
                </Tooltip>
              }
              value={metrics?.page_speed || 0}
              suffix="/ 100"
              valueStyle={{ color: getScoreColor(metrics?.page_speed || 0) }}
            />
            
            <Statistic
              title={
                <Tooltip title="Core Web Vitals: LCP, FID, CLS">
                  <Space>
                    <span>Core Web Vitals</span>
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  </Space>
                </Tooltip>
              }
              value={metrics?.core_web_vitals || 0}
              suffix="/ 100"
              valueStyle={{ color: getScoreColor(metrics?.core_web_vitals || 0) }}
            />
            
            <Statistic
              title={
                <Tooltip title="JavaScript errors and failed requests">
                  <Space>
                    <span>Error Rate</span>
                    {(metrics?.error_rate || 0) < 20 ? 
                      <CheckCircleOutlined style={{ color: '#52c41a' }} /> : 
                      <WarningOutlined style={{ color: '#faad14' }} />
                    }
                  </Space>
                </Tooltip>
              }
              value={metrics?.error_rate || 0}
              suffix="%"
              valueStyle={{ color: (metrics?.error_rate || 0) < 20 ? '#52c41a' : '#faad14' }}
            />
            
            <Statistic
              title={
                <Tooltip title="User satisfaction based on interactions and feedback">
                  <Space>
                    <span>User Satisfaction</span>
                    <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  </Space>
                </Tooltip>
              }
              value={metrics?.user_satisfaction || 0}
              suffix="/ 100"
              valueStyle={{ color: getScoreColor(metrics?.user_satisfaction || 0) }}
            />
          </Space>
        </Col>
      </Row>

      <Alert
        message="How is this calculated?"
        description={
          <div>
            <p style={{ marginBottom: 8 }}>Your Live Experience Score is calculated using:</p>
            <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
              <li><strong>Page Speed (30%):</strong> Loading performance and response times</li>
              <li><strong>Core Web Vitals (30%):</strong> LCP, FID, CLS metrics from Google</li>
              <li><strong>Error Rate (20%):</strong> JavaScript errors and failed requests</li>
              <li><strong>User Satisfaction (20%):</strong> Interaction quality and feedback</li>
            </ul>
          </div>
        }
        type="info"
        showIcon
        style={{ marginTop: 24 }}
      />
    </Card>
  );
};

export default LiveExperienceScore;
